import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            try {
                setUser(JSON.parse(savedUser));
            } catch {
                localStorage.removeItem('user');
                localStorage.removeItem('token');
            }
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        setLoading(true);
        try {
            const result = await authService.login({ email, password });
            const userData = result.data;

            // Salva token separatamente per gli interceptor di api.js
            localStorage.setItem('token', userData.token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);

            toast.success('Accesso effettuato con successo!');
            return { success: true, data: userData };
        } catch (error) {
            const message = error?.message || 'Credenziali non valide';
            toast.error(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const register = async (userData) => {
        setLoading(true);
        try {
            const result = await authService.register(userData);
            const newUser = result.data;

            localStorage.setItem('token', newUser.token);
            localStorage.setItem('user', JSON.stringify(newUser));
            setUser(newUser);

            toast.success('Registrazione completata con successo!');
            return { success: true, data: newUser };
        } catch (error) {
            const message = error?.message || 'Errore durante la registrazione';
            toast.error(message);
            return { success: false, message };
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        toast.success('Logout effettuato con successo');
    };

    const updateProfile = async (profileData) => {
        try {
            const result = await authService.updateProfile(profileData);
            const updatedUser = { ...user, ...result.data };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            toast.success('Profilo aggiornato con successo!');
            return { success: true, data: updatedUser };
        } catch (error) {
            const message = error?.message || 'Errore durante l\'aggiornamento del profilo';
            toast.error(message);
            return { success: false, message };
        }
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        updateProfile,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};